import { cn } from '../cn';
import { ButtonA } from '../Input2';
import { Alerts, General } from '../StrokeIcons';
import { carrierFailureBlocksOutcomes, TWILIO_CALL_BLOCKED_CODE, TWILIO_GEO_PERMISSION_CODE } from './softphoneStatus';

export { carrierFailureBlocksOutcomes, TWILIO_CALL_BLOCKED_CODE };

interface CarrierErrorCopy {
  title: string;
  body: string;
  /** Concrete "how to fix" steps — only for errors where the fix is on our side of the carrier. */
  steps?: ReadonlyArray<string>;
}

const TWILIO_SUPPORT_URL = 'https://help.twilio.com/';
const TWILIO_TRUST_HUB_URL = 'https://console.twilio.com/us1/develop/trust-hub/customer-profiles';

function twilioErrorDocUrl(code: number): string {
  return `https://www.twilio.com/docs/api/errors/${code}`;
}

/**
 * Plain-language copy per carrier code. Twilio's own text for these is "Phone number is
 * blacklisted" / "invalid phone number" — accurate to Twilio, misleading to a rep. Each entry says
 * what actually happened, whether it's about THIS call or the whole account, and what to do.
 */
const CARRIER_ERROR_COPY: Readonly<Record<number, CarrierErrorCopy>> = {
  [TWILIO_CALL_BLOCKED_CODE]: {
    title: 'Twilio blocked this call',
    body:
      'Twilio refused to connect the call before it rang (error 13225, “call blocked by Twilio”). ' +
      'This isn’t this prospect’s number or your caller ID — Twilio has restricted outbound calling on ' +
      'your account, so every call will fail the same way until it’s lifted.',
    steps: [
      'Ask a team admin to open a Twilio support ticket quoting error 13225 and the date it started.',
      'In Twilio Trust Hub, make sure the account has an approved Business customer profile — a Starter or Individual profile may no longer qualify for US calls.',
      'Don’t log this as “No answer” — the prospect was never rung.',
    ],
  },
  13224: {
    title: 'Twilio says this number is invalid',
    body:
      'The carrier rejected the dialed number as invalid or unassigned (error 13224). ' +
      'Mark it as a wrong number so it isn’t dialed again.',
  },
  [TWILIO_GEO_PERMISSION_CODE]: {
    title: 'Calls to this country aren’t enabled',
    body:
      'Twilio’s geographic permissions for your account don’t allow calls to this destination (error 13227). ' +
      'A team admin can enable the country under Voice → Geographic Permissions in the Twilio console.',
  },
};

function copyFor(code: number | null): CarrierErrorCopy {
  const known = code != null ? CARRIER_ERROR_COPY[code] : undefined;
  if (known) return known;
  if (code != null) {
    return {
      title: 'The carrier couldn’t place this call',
      body: `The carrier rejected the call before it rang (error ${code}).`,
    };
  }
  // No code: the carrier only reported the leg as failed. That may be a far-end failure, or (on
  // carriers that map every unlisted hangup cause to "failed") an ordinary termination — so make
  // no claim about whether the prospect was reached.
  return {
    title: 'The carrier reported this call as failed',
    body: 'The call didn’t complete normally and the carrier gave no reason. If nobody picked up, log it as usual.',
  };
}

export interface CallFailedNoticeProps {
  /** The carrier's error code, or null when it recorded none. */
  code: number | null;
  className?: string;
}

/**
 * Post-call notice for a dial the CARRIER refused — the case the browser can't see on its own
 * (the WebRTC leg connects, the PSTN bridge is rejected, the rep sees a plain "Call ended"). Names
 * the real cause, says whether it's this call or the whole account, and links the fix. Rendered by
 * every calling surface (floating dialer, inline power-dialer, extension) in place of the bare
 * "Call ended" line; the surface decides whether the outcome picker still makes sense (see
 * `carrierFailureIsAccountBlock`).
 */
export function CallFailedNotice({ code, className }: CallFailedNoticeProps) {
  const copy = copyFor(code);
  const accountBlock = code === TWILIO_CALL_BLOCKED_CODE;

  return (
    <div
      role="alert"
      className={cn(
        'flex w-full flex-col gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-left dark:border-red-500/30 dark:bg-red-500/10',
        className,
      )}
      data-test="call-failed-notice">
      <div className="flex items-start gap-2">
        <Alerts.AlertTriangle className="mt-0.5 size-5 shrink-0 text-red-600 dark:text-red-400" />
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-sm font-semibold text-red-700 dark:text-red-300" data-test="call-failed-title">
            {copy.title}
          </span>
          <p className="text-xs leading-relaxed text-red-700/90 dark:text-red-200/80">{copy.body}</p>
        </div>
      </div>

      {copy.steps && (
        <ol
          className="list-decimal space-y-1 pl-9 text-xs text-red-700/90 dark:text-red-200/80"
          data-test="call-failed-steps">
          {copy.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      )}

      <div className="flex flex-wrap gap-2 pl-7">
        {accountBlock && (
          <>
            <ButtonA
              href={TWILIO_SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              size="xs"
              color="red"
              tone="outline"
              rIcon={<General.LinkExternal01 />}
              data-test="call-failed-support-link">
              Twilio support
            </ButtonA>
            <ButtonA
              href={TWILIO_TRUST_HUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              size="xs"
              color="red"
              tone="outline"
              rIcon={<General.LinkExternal01 />}
              data-test="call-failed-trust-hub-link">
              Trust Hub
            </ButtonA>
          </>
        )}
        {code != null && (
          <ButtonA
            href={twilioErrorDocUrl(code)}
            target="_blank"
            rel="noopener noreferrer"
            size="xs"
            text
            rIcon={<General.LinkExternal01 />}
            data-test="call-failed-doc-link">
            About error {code}
          </ButtonA>
        )}
      </div>
    </div>
  );
}
